import { Renderer2D } from '../rendering/Renderer2D.js'
import { Player } from '../player/Player.js'
import { World } from '../world/World.js'
import { InputManager } from './InputManager.js'
import { SaveManager } from './SaveManager.js'
import { EnemyManager } from '../entities/EnemyManager.js'
import { ItemManager } from '../items/ItemManager.js'
import { HUD } from '../ui/HUD.js'
import { DayNightCycle } from './DayNightCycle.js'
import { SWORD_SPRITE } from '../rendering/Sprites.js'
import { drawPixelSpriteToCanvas, PIXEL_SCALE } from '../rendering/PixelGrid.js'
import { getWeapon } from '../items/ItemTypes.js'
import {
  HUD_ARROW_MATERIAL_SPRITES,
  HUD_SHIELD_MATERIAL_SPRITES,
  HUD_SHIELD_SPRITE,
  HUD_SWORD_MATERIAL_SPRITES
} from '../rendering/UISprites.js'
import { SHOPKEEPER_SPRITES } from '../rendering/CharacterSpriteSheets.js'

export const SHOP_EQUIPMENT_BY_AREA = Object.freeze({
  forest: Object.freeze({
    sword: Object.freeze({ id: 'stone_sword', price: 5 }),
    shield: Object.freeze({ id: 'oak_shield', name: 'Oak Shield', defenseHearts: 0.5, price: 4 }),
    arrows: Object.freeze({ id: 'arrow', name: 'Oak Arrows', count: 10, price: 3 })
  }),
  graveyard: Object.freeze({
    sword: Object.freeze({ id: 'bone_sword', price: 7 }),
    shield: Object.freeze({ id: 'bone_shield', name: 'Bone Shield', defenseHearts: 0.75, price: 6 }),
    arrows: Object.freeze({ id: 'bone_arrow', name: 'Bone Arrows', count: 8, price: 4 })
  }),
  caves: Object.freeze({
    sword: Object.freeze({ id: 'diamond_sword', price: 10 }),
    shield: Object.freeze({ id: 'crystal_shield', name: 'Crystal Shield', defenseHearts: 1, price: 9 }),
    arrows: Object.freeze({ id: 'crystal_arrow', name: 'Crystal Arrows', count: 8, price: 5 })
  }),
  emberkeep: Object.freeze({
    sword: Object.freeze({ id: 'ember_sword', price: 13 }),
    shield: Object.freeze({ id: 'ember_shield', name: 'Ember Shield', defenseHearts: 1.25, price: 12 }),
    arrows: Object.freeze({ id: 'fire_arrow', name: 'Fire Arrows', count: 6, price: 6 })
  }),
  purpledooms: Object.freeze({
    sword: Object.freeze({ id: 'legendary_sword', price: 16 }),
    shield: Object.freeze({ id: 'dark_knight_shield', name: 'Dark Knight Shield', defenseHearts: 1.5, price: 15 }),
    arrows: Object.freeze({ id: 'shadow_arrow', name: 'Shadow Arrows', count: 6, price: 8 })
  }),
  village: Object.freeze({
    sword: Object.freeze({ id: 'royal_sword', price: 25 }),
    shield: Object.freeze({ id: 'sun_guard', name: 'Sun Guard', defenseHearts: 2, price: 22 }),
    arrows: Object.freeze({ id: 'light_arrow', name: 'Light Arrows', count: 10, price: 12 })
  })
})

export class Game {
  constructor() {
    this.renderer = new Renderer2D(document.getElementById('game-container'))
    
    this.lastTime = 0
    this.isRunning = false
    this.isPaused = false
    
    this.input = new InputManager(this)
    this.saveManager = new SaveManager()
    this.hud = new HUD(this)
    this.dayNight = new DayNightCycle(this)
    
    this.world = null
    this.player = null
    this.enemyManager = null
    this.itemManager = null

    this.activeMenuButtons = []
    this.activeMenuIndex = 0
    this.sleepOverlay = document.getElementById('sleep-overlay')
  }
  
  start() {
    this.world = new World(this)
    this.player = new Player(this)
    this.world.markVisitedFromPosition(this.player.x, this.player.y)
    this.enemyManager = new EnemyManager(this)
    this.itemManager = new ItemManager(this)
    
    this.player.giveStartingItems()
    
    this.isRunning = true
    this.lastTime = performance.now()
    this.animate()
  }
  
  loadAndStart() {
    const saveData = this.saveManager.load()
    if (saveData) {
      this.world = new World(this, saveData.world)
      this.player = new Player(this, saveData.player)
      this.world.markVisitedFromPosition(this.player.x, this.player.y)
      this.enemyManager = new EnemyManager(this, saveData.enemies)
      this.itemManager = new ItemManager(this, saveData.items)
      this.dayNight.load(saveData.time)
    } else {
      this.start()
      return
    }
    
    this.isRunning = true
    this.lastTime = performance.now()
    this.animate()
  }
  
  loadFromAutoSave(saveData) {
    this.world = new World(this, saveData.world)
    this.player = new Player(this, saveData.player)
    this.world.markVisitedFromPosition(this.player.x, this.player.y)
    this.enemyManager = new EnemyManager(this, saveData.enemies)
    this.itemManager = new ItemManager(this, saveData.items)
    this.dayNight.load(saveData.time)
    
    this.isRunning = true
    this.lastTime = performance.now()
    this.animate()
  }
  
  animate() {
    if (!this.isRunning) return
    
    requestAnimationFrame(() => this.animate())
    
    const currentTime = performance.now()
    const delta = Math.min((currentTime - this.lastTime) / 1000, 0.1) // Cap at 100ms
    this.lastTime = currentTime
    
    // Always poll gamepad (even when paused for menu navigation)
    this.input.update()
    
    if (this.isPaused) {
      this.render()
      return
    }
    
    // Update
    this.dayNight.update(delta)
    this.player.update(delta)
    this.world.update(delta)
    this.enemyManager.update(delta)
    this.itemManager.update(delta)
    this.hud.update()
    
    // Clear just-pressed keys after update
    this.input.clearJustPressed()
    
    // Auto-save for hot reload (every 2 seconds)
    this.autoSaveTimer = (this.autoSaveTimer || 0) + delta
    if (this.autoSaveTimer > 2) {
      this.autoSaveTimer = 0
      this.quickSave()
    }
    
    // Render
    this.render()
  }
  
  render() {
    const insideHome = this.world.isInsideHome(this.player.x, this.player.y)

    // Clear with background color based on time of day
    const bgColor = insideHome ? '#09070a' : this.dayNight.getBackgroundColor()
    this.renderer.clear(bgColor)
    
    // Center camera on player
    this.renderer.centerOn(this.player.x, this.player.y)

    if (insideHome) {
      this.renderer.withWorldTileBounds(
        this.world.getHomeInteriorBounds(),
        () => {
          this.world.render(this.renderer, 'below', this.player.y)
          this.player.render(this.renderer)
          this.world.render(this.renderer, 'above', this.player.y)
        }
      )
    } else {
      // Render world below the player (ground + raised/decorations behind)
      this.world.render(this.renderer, 'below', this.player.y)
    
      // Render items
      this.itemManager.render(this.renderer)
    
      // Render enemies
      this.enemyManager.render(this.renderer)
    
      // Render player
      this.player.render(this.renderer)
    
      // Render world above the player (raised/decorations in front)
      this.world.render(this.renderer, 'above', this.player.y)
    }
    
    // Apply day/night overlay
    const overlay = this.dayNight.getOverlay()
    if (overlay) {
      this.renderer.applyOverlay(overlay.color, overlay.alpha)
    }
    
    // Always apply subtle vignette for atmosphere
    this.renderer.applyVignette(0.2)

    if (this.sleepOverlay) {
      this.sleepOverlay.hidden = this.player?.isSleeping !== true
    }
  }
  
  pause() {
    this.isPaused = true
    document.getElementById('pause-menu').style.display = 'flex'
    const saveButton = document.getElementById('save-btn')
    if (saveButton) saveButton.textContent = 'SAVE GAME'
    const saveStatus = document.getElementById('save-status')
    if (saveStatus) saveStatus.hidden = true
    const goHomeButton = document.getElementById('go-home-btn')
    const alreadyHome = this.world?.isInsideHome(this.player?.x, this.player?.y) === true
    if (goHomeButton) {
      // Keep this option in controller/touch navigation while at home. The
      // action itself safely returns false, so selecting it simply does nothing.
      goHomeButton.disabled = false
      goHomeButton.classList.toggle('is-unavailable', alreadyHome)
      goHomeButton.textContent = alreadyHome ? '✕ GO HOME' : 'GO HOME'
      goHomeButton.setAttribute('aria-disabled', alreadyHome ? 'true' : 'false')
      goHomeButton.setAttribute(
        'aria-label',
        alreadyHome ? 'Go Home unavailable: already home' : 'Go Home'
      )
    }
    this.setActiveMenuButtons([
      'resume-btn',
      'controller-controls-btn',
      'touch-control-mode-btn',
      'save-btn',
      'go-home-btn',
      'quit-btn'
    ])
  }

  showControlsGuide(mode = 'controller') {
    if (!this.isRunning) return false
    const guide = document.getElementById('controls-guide')
    const title = document.getElementById('controls-guide-title')
    const list = document.getElementById('controls-guide-list')
    const controllerDiagram = document.getElementById('controller-controls-diagram')
    if (!guide || !title || !list || !controllerDiagram) return false

    const touch = mode === 'touch'
    const controls = touch
      ? [
          ['DRAG', 'Walk with the joystick'],
          ['TAP WORLD', 'Swing the sword or use a nearby door'],
          ['SWORD', 'Attack or open a nearby chest'],
          ['ARROW', 'Shoot an arrow'],
          ['SHIELD', 'Hold to block'],
          ['B', 'Get off furniture or cancel'],
          ['BAG', 'Open fruit and potions'],
          ['MAP', 'Tap to enlarge; tap again to close'],
          ['PAUSE', 'Save, go home, or change touch style']
        ]
      : []

    title.textContent = touch ? 'TOUCH CONTROLS' : 'CONTROLLER CONTROLS'
    list.hidden = !touch
    controllerDiagram.hidden = touch
    list.replaceChildren(...controls.flatMap(([input, action]) => {
      const inputElement = document.createElement('span')
      inputElement.className = 'control-input'
      inputElement.textContent = input
      const actionElement = document.createElement('span')
      actionElement.textContent = action
      return [inputElement, actionElement]
    }))
    this.controlsGuideWasPaused = this.isPaused
    this.isPaused = true
    guide.style.display = 'flex'
    this.setActiveMenuButtons(['controls-guide-close-btn'])
    return true
  }

  closeControlsGuide() {
    const guide = document.getElementById('controls-guide')
    if (!guide || guide.style.display !== 'flex') return false
    guide.style.display = 'none'
    const returnToPause = this.controlsGuideWasPaused === true &&
      document.getElementById('pause-menu').style.display === 'flex'
    this.controlsGuideWasPaused = false
    if (returnToPause) {
      this.isPaused = false
      this.pause()
    } else {
      this.isPaused = false
      this.clearActiveMenuButtons()
    }
    return true
  }

  goHome() {
    if (!this.world?.sendPlayerHome?.()) return false
    this.resume()
    return true
  }

  resume() {
    this.isPaused = false
    document.getElementById('pause-menu').style.display = 'none'
    document.getElementById('death-screen').style.display = 'none'
    document.getElementById('item-popup').style.display = 'none'
    this.clearActiveMenuButtons()
  }
  
  quickSave() {
    const saveData = {
      player: this.player.serialize(),
      world: this.world.serialize(),
      enemies: this.enemyManager.serialize(),
      items: this.itemManager.serialize(),
      time: this.dayNight.serialize()
    }
    try {
      localStorage.setItem('poe-quest-autosave', JSON.stringify(saveData))
      return true
    } catch {
      // Storage can be unavailable or full on mobile browsers. Autosave must
      // never terminate the animation loop and freeze the running game.
      return false
    }
  }
  
  saveGame() {
    const saveData = {
      player: this.player.serialize(),
      world: this.world.serialize(),
      enemies: this.enemyManager.serialize(),
      items: this.itemManager.serialize(),
      time: this.dayNight.serialize()
    }
    const saved = this.saveManager.save(saveData)
    if (saved) this.quickSave()
    const saveButton = document.getElementById('save-btn')
    if (saveButton) saveButton.textContent = 'SAVE GAME'
    const saveStatus = document.getElementById('save-status')
    if (saveStatus) {
      saveStatus.textContent = saved ? 'GAME SAVED' : 'SAVE FAILED'
      saveStatus.hidden = false
    }
    return saved
  }
  
  load() {
    const saveData = this.saveManager.load()
    if (saveData) {
      this.player.deserialize(saveData.player)
      this.world.deserialize(saveData.world)
      this.world.markVisitedFromPosition(this.player.x, this.player.y)
      this.enemyManager.deserialize(saveData.enemies)
      this.itemManager.deserialize(saveData.items)
      this.dayNight.deserialize(saveData.time)
      alert('Game Loaded!')
    } else {
      alert('No save data found!')
    }
    this.resume()
  }
  
  quitToTitle() {
    this.isRunning = false
    this.isPaused = false
    document.getElementById('pause-menu').style.display = 'none'
    document.getElementById('death-screen').style.display = 'none'
    this.clearActiveMenuButtons()
    document.getElementById('start-screen').style.display = 'flex'
  }
  
  showDeathScreen() {
    this.isPaused = true
    document.getElementById('death-screen').style.display = 'flex'
    this.setActiveMenuButtons(['death-continue-btn'])
  }

  showBossHealth(name) {
    document.getElementById('boss-name').textContent = name
    document.getElementById('boss-health').style.display = 'block'
  }

  updateBossHealth(ratio) {
    const bar = document.getElementById('boss-health-bar')
    const fill = document.getElementById('boss-health-fill')
    const width = Math.round((bar.clientWidth * Math.max(0, Math.min(1, ratio))) / PIXEL_SCALE) * PIXEL_SCALE
    fill.style.width = `${Math.min(bar.clientWidth, width)}px`
  }

  hideBossHealth() {
    document.getElementById('boss-health').style.display = 'none'
  }

  showVictoryScreen() {
    this.world.completeDarkKingVictory?.()
    this.isPaused = true
    document.getElementById('victory-screen').style.display = 'flex'
    this.setActiveMenuButtons(['victory-continue-btn'])
    this.quickSave()
  }

  dismissVictoryScreen() {
    document.getElementById('victory-screen').style.display = 'none'
    this.isPaused = false
    this.clearActiveMenuButtons()
  }

  showUpgradeShop(shop) {
    this.activeShop = shop
    this.isPaused = true
    document.getElementById('upgrade-shop').style.display = 'flex'
    const shopkeeper = SHOPKEEPER_SPRITES[shop.areaId] || SHOPKEEPER_SPRITES.forest
    const shopkeeperImage = document.getElementById('shopkeeper-portrait-image')
    shopkeeperImage.src = shopkeeper.imageUrl
    shopkeeperImage.alt = `${shop.areaId} shopkeeper`
    document.getElementById('shopkeeper-card').hidden =
      this.itemManager?.isShopkeeperPresent(shop) !== true
    document.getElementById('shopkeeper-stall').dataset.area = shop.areaId
    const levelIndex = this.world.zones.findIndex(zone => zone.name === shop.areaId)
    const isPurpleDooms = levelIndex === 4
    // Every postgame village stall is fruit-only, including shops restored
    // from older saves that predate the fruitOnly flag.
    const fruitOnly = this.isFruitOnlyShop(shop)
    const allStock = shop.allStock === true
    for (const buttonId of [
      'shop-sword-btn',
      'shop-shield-btn',
      'shop-arrow-btn',
      'shop-potion-btn'
    ]) {
      document.getElementById(buttonId).hidden = fruitOnly
    }
    const shopFruits = [
      ['shop-apple-btn', 0, false],
      ['shop-banana-btn', 0, false],
      ['shop-dragon-fruit-btn', 0, false],
      ['shop-golden-pineapple-btn', 1, false],
      ['shop-moon-melon-btn', 2, false],
      ['shop-watermelon-btn', 3, false],
      ['shop-golden-kiwi-btn', 4, true],
      ['shop-star-fruit-btn', 4, true]
    ]
    for (const [buttonId, unlockLevel, soldInPurpleDooms] of shopFruits) {
      const button = document.getElementById(buttonId)
      const hidden = !allStock && !fruitOnly && isPurpleDooms && !soldInPurpleDooms
      const locked = !allStock && !fruitOnly && !hidden && levelIndex < unlockLevel
      button.hidden = hidden
      button.disabled = locked
      button.classList.toggle('is-locked', locked)
    }
    if (!fruitOnly) this.updateShopEquipmentCards()
    this.updateUpgradeShop()
    this.setActiveMenuButtons([
      ...(fruitOnly ? [] : [
        'shop-sword-btn',
        'shop-shield-btn',
        'shop-arrow-btn',
        'shop-potion-btn'
      ]),
      'shop-apple-btn',
      'shop-banana-btn',
      'shop-dragon-fruit-btn',
      'shop-golden-pineapple-btn',
      'shop-moon-melon-btn',
      'shop-golden-kiwi-btn',
      'shop-watermelon-btn',
      'shop-star-fruit-btn',
      'shop-close-btn'
    ])
  }

  updateShopEquipmentCards() {
    const equipment = SHOP_EQUIPMENT_BY_AREA[this.activeShop?.areaId]
      || SHOP_EQUIPMENT_BY_AREA.forest
    const weapon = getWeapon(equipment.sword.id)
    const arrowName = this.player.hasBow
      ? equipment.arrows.name
      : `${equipment.arrows.name.replace(/\s*ARROWS?$/i, '')} BOW + ARROWS`
    const cards = [
      ['shop-sword-btn', weapon.name, `ATK ${weapon.damage}`, equipment.sword.price],
      ['shop-shield-btn', equipment.shield.name, `${equipment.shield.defenseHearts} HEART`, equipment.shield.price],
      ['shop-arrow-btn', arrowName, `×${equipment.arrows.count}`, equipment.arrows.price]
    ]
    for (const [buttonId, name, stat, price] of cards) {
      const button = document.getElementById(buttonId)
      button.querySelector('.shop-item-name').textContent = name.toUpperCase()
      button.querySelector('.equipment-stat').textContent = stat
      button.querySelector('.equipment-price').textContent = price
      button.setAttribute('aria-label', `${name}, ${stat}, for ${price} coins`)
    }
    drawPixelSpriteToCanvas(
      document.querySelector('#shop-sword-btn .shop-item-icon'),
      HUD_SWORD_MATERIAL_SPRITES[weapon.id]
    )
    drawPixelSpriteToCanvas(
      document.querySelector('#shop-arrow-btn .shop-item-icon'),
      HUD_ARROW_MATERIAL_SPRITES[equipment.arrows.id]
    )
    drawPixelSpriteToCanvas(
      document.querySelector('#shop-shield-btn .shop-item-icon'),
      HUD_SHIELD_MATERIAL_SPRITES[equipment.shield.id] || HUD_SHIELD_SPRITE
    )
  }

  updateUpgradeShop(message = '') {
    const balance = this.player.coins
    document.getElementById('shop-coin-count').textContent = balance
    document.getElementById('shop-message').textContent = message
    this.hud.renderCoins(balance)
    this.hud.lastCoins = balance
  }

  isFruitOnlyShop(shop = this.activeShop) {
    return shop?.postgame === true || shop?.fruitOnly === true
  }

  buyShopUpgrade(kind) {
    if (this.isFruitOnlyShop()) return false
    const equipment = SHOP_EQUIPMENT_BY_AREA[this.activeShop?.areaId]
      || SHOP_EQUIPMENT_BY_AREA.forest
    const offer = kind === 'potion' ? { price: 2 } : equipment[kind === 'arrow' ? 'arrows' : kind]
    const price = offer?.price
    if (!price) return false

    if (kind === 'sword' && this.player.weaponId === offer.id) {
      this.updateUpgradeShop('YOU ALREADY HAVE THIS SWORD')
      return false
    }
    if (kind === 'shield' && this.player.shieldId === offer.id) {
      this.updateUpgradeShop('YOU ALREADY HAVE THIS SHIELD')
      return false
    }
    if (!this.player.spendCoins(price)) {
      this.updateUpgradeShop(`NEED ${price} COINS`)
      return false
    }

    if (kind === 'sword') {
      if (!this.player.hasSword) {
        this.player.addCoins(price)
        this.updateUpgradeShop('FIND A SWORD FIRST')
        return false
      }
      const weapon = getWeapon(offer.id)
      if (!this.player.equipWeapon(weapon)) {
        this.player.addCoins(price)
        this.updateUpgradeShop('YOUR SWORD IS ALREADY STRONGER')
        return false
      }
      this.updateUpgradeShop(`${weapon.name.toUpperCase()} EQUIPPED`)
    } else if (kind === 'shield') {
      if (!this.player.equipShield(offer)) {
        this.player.addCoins(price)
        this.updateUpgradeShop('YOUR SHIELD IS ALREADY STRONGER')
        return false
      }
      this.updateUpgradeShop(`${offer.name.toUpperCase()} EQUIPPED`)
    } else if (kind === 'arrow') {
      const receivedBow = this.player.equipBow()
      this.player.addArrows(offer.id, offer.count)
      this.updateShopEquipmentCards()
      this.updateUpgradeShop(receivedBow
        ? `BOW EQUIPPED + ${offer.count} ${offer.name.toUpperCase()}`
        : `${offer.count} ${offer.name.toUpperCase()} ADDED`)
    } else {
      this.player.addFruit('healthPotion')
      this.updateUpgradeShop('HEALTH POTION ADDED TO YOUR BAG')
    }
    this.quickSave()
    return true
  }

  buyShopFruit(kind) {
    const fruit = {
      apple: { name: 'APPLE', price: 1, hearts: 0.5, unlockLevel: 0 },
      banana: { name: 'BANANA', price: 2, hearts: 1, unlockLevel: 0 },
      dragonFruit: { name: 'DRAGON FRUIT', price: 4, hearts: 2, unlockLevel: 0 },
      goldenPineapple: { name: 'GOLDEN PINEAPPLE', price: 10, hearts: 3, unlockLevel: 1 },
      moonMelon: { name: 'AVOCADO', price: 12, hearts: 4, unlockLevel: 2 },
      goldenKiwi: { name: 'GOLDEN KIWI', price: 13, hearts: 4.5, unlockLevel: 4 },
      watermelon: { name: 'WATERMELON', price: 14, hearts: 5, unlockLevel: 3 },
      starFruit: { name: 'STAR FRUIT', price: 18, hearts: 6, unlockLevel: 4 }
    }[kind]
    if (!fruit) return false
    const levelIndex = this.world.zones.findIndex(zone => zone.name === this.activeShop?.areaId)
    if (!this.isFruitOnlyShop() && !this.activeShop?.allStock && levelIndex < fruit.unlockLevel) {
      this.updateUpgradeShop('LOCKED UNTIL A LATER LEVEL')
      return false
    }
    if (!this.player.spendCoins(fruit.price)) {
      this.updateUpgradeShop(`NEED ${fruit.price} COIN${fruit.price === 1 ? '' : 'S'}`)
      return false
    }

    this.player.addFruit(kind, 1)
    this.updateUpgradeShop(`${fruit.name} ADDED TO YOUR BAG`)
    this.quickSave()
    return true
  }

  closeUpgradeShop() {
    document.getElementById('upgrade-shop').style.display = 'none'
    this.activeShop = null
    this.isPaused = false
    this.clearActiveMenuButtons()
  }
  
  respawn() {
    document.getElementById('death-screen').style.display = 'none'
    const preserveDarkKingFight = this.player.respawn()
    if (!preserveDarkKingFight) {
      this.enemyManager.respawnAheadFromPosition(this.player.x, this.player.y)
    } else {
      const checkpoint = this.world.darkKingCheckpoint
      this.enemyManager.clearEnemiesInRoom?.(checkpoint.x, checkpoint.y)
    }
    this.isPaused = false
    this.clearActiveMenuButtons()
  }

  newGame() {
    try {
      localStorage.removeItem('poe-quest-autosave')
      localStorage.removeItem('poes_quest_save')
    } catch {
      // Reloading into a new game must work even when storage is blocked.
    }
    window.location.reload()
  }

  setActiveMenuButtons(buttonIds) {
    this.activeMenuButtons = buttonIds
      .map(id => document.getElementById(id))
      .filter(button => button && !button.hidden && !button.disabled)
    this.activeMenuIndex = 0
    this.updateMenuHighlight()
  }

  clearActiveMenuButtons() {
    for (const btn of this.activeMenuButtons) {
      btn.classList.remove('active')
    }
    this.activeMenuButtons = []
    this.activeMenuIndex = 0
  }

  updateMenuHighlight() {
    for (let i = 0; i < this.activeMenuButtons.length; i++) {
      const btn = this.activeMenuButtons[i]
      if (i === this.activeMenuIndex) {
        btn.classList.add('active')
        btn.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
      } else {
        btn.classList.remove('active')
      }
    }
  }

  navigateMenu(direction) {
    if (!this.isPaused || this.activeMenuButtons.length === 0) return
    const len = this.activeMenuButtons.length
    this.activeMenuIndex = (this.activeMenuIndex + direction + len) % len
    this.updateMenuHighlight()
  }

  navigateMenuDirection(x, y) {
    if (!this.isPaused || this.activeMenuButtons.length === 0) return
    const shopOpen = document.getElementById('upgrade-shop').style.display === 'flex'
    const fruitOpen = document.getElementById('fruit-menu').style.display === 'flex'
    if (fruitOpen) {
      const len = this.activeMenuButtons.length
      if (x !== 0) {
        this.activeMenuIndex = (this.activeMenuIndex + x + len) % len
      } else if (y < 0 && this.activeMenuIndex >= 4) {
        this.activeMenuIndex -= 4
      } else if (y > 0 && this.activeMenuIndex + 4 < len) {
        this.activeMenuIndex += 4
      }
      this.updateMenuHighlight()
      return
    }
    if (!shopOpen) {
      this.navigateMenu(y || x)
      return
    }

    const closeIndex = this.activeMenuButtons.length - 1
    const itemCount = closeIndex
    let next = this.activeMenuIndex
    if (next === closeIndex) {
      if (y < 0) next = itemCount - 1
    } else if (x !== 0) {
      next = (next + x + itemCount) % itemCount
    } else if (y < 0) {
      next = next >= 3 ? next - 3 : next
    } else if (y > 0) {
      next = next + 3 < itemCount ? next + 3 : closeIndex
    }

    this.activeMenuIndex = next
    this.updateMenuHighlight()
  }

  activateMenuSelection() {
    if (!this.isPaused || this.activeMenuButtons.length === 0) return
    const selected = this.activeMenuButtons[this.activeMenuIndex]
    if (selected) selected.click()
  }

  cancelActiveMenu() {
    if (document.getElementById('controls-guide').style.display === 'flex') {
      return this.closeControlsGuide()
    } else if (document.getElementById('fruit-menu').style.display === 'flex') {
      this.closeFruitMenu()
      return true
    } else if (document.getElementById('upgrade-shop').style.display === 'flex') {
      this.closeUpgradeShop()
      return true
    } else if (document.getElementById('item-popup').style.display === 'flex') {
      this.dismissItemPopup()
      return true
    } else if (document.getElementById('victory-screen').style.display === 'flex') {
      this.dismissVictoryScreen()
      return true
    } else if (document.getElementById('pause-menu').style.display === 'flex') {
      this.resume()
      return true
    }
    return false
  }

  showFruitMenu() {
    if (!this.isRunning || this.isPaused) return
    this.isPaused = true
    document.getElementById('fruit-menu').style.display = 'flex'
    this.updateFruitLocks()
    this.updateFruitMenu()
    this.setActiveMenuButtons([
      'fruit-health-potion-btn',
      'fruit-banana-btn',
      'fruit-apple-btn',
      'fruit-dragon-fruit-btn',
      'fruit-golden-pineapple-btn',
      'fruit-moon-melon-btn',
      'fruit-golden-kiwi-btn',
      'fruit-watermelon-btn',
      'fruit-star-fruit-btn'
    ])
  }

  updateFruitLocks() {
    const unlocks = [
      ['fruit-golden-pineapple-btn', 'goldenPineapple', 1, 'golden pineapple', '3 hearts'],
      ['fruit-moon-melon-btn', 'moonMelon', 2, 'avocado', '4 hearts'],
      ['fruit-watermelon-btn', 'watermelon', 3, 'watermelon', '5 hearts'],
      ['fruit-golden-kiwi-btn', 'goldenKiwi', 4, 'golden kiwi', '4 and a half hearts'],
      ['fruit-star-fruit-btn', 'starFruit', 4, 'star fruit', '6 hearts']
    ]

    for (const [buttonId, fruitId, level, name, healing] of unlocks) {
      const button = document.getElementById(buttonId)
      const locked = (
        this.player.highestLevelReached < level &&
        (this.player.fruit[fruitId] || 0) === 0
      )
      button.hidden = false
      button.disabled = locked
      button.classList.toggle('is-locked', locked)
      button.setAttribute(
        'aria-label',
        locked
          ? `${name} locked until a later level; restores ${healing}`
          : `Eat ${name} to restore ${healing}`
      )
    }
  }

  updateFruitMenu(message = '') {
    this.hud.renderHearts(
      this.player.hearts,
      this.player.maxHearts,
      document.getElementById('fruit-menu-hearts')
    )
    document.getElementById('fruit-health-potion-count').textContent = `×${this.player.fruit.healthPotion || 0}`
    document.getElementById('fruit-banana-count').textContent = `×${this.player.fruit.banana || 0}`
    document.getElementById('fruit-apple-count').textContent = `×${this.player.fruit.apple || 0}`
    document.getElementById('fruit-dragon-fruit-count').textContent = `×${this.player.fruit.dragonFruit || 0}`
    document.getElementById('fruit-golden-pineapple-count').textContent = `×${this.player.fruit.goldenPineapple || 0}`
    document.getElementById('fruit-moon-melon-count').textContent = `×${this.player.fruit.moonMelon || 0}`
    document.getElementById('fruit-golden-kiwi-count').textContent = `×${this.player.fruit.goldenKiwi || 0}`
    document.getElementById('fruit-watermelon-count').textContent = `×${this.player.fruit.watermelon || 0}`
    document.getElementById('fruit-star-fruit-count').textContent = `×${this.player.fruit.starFruit || 0}`
    document.getElementById('fruit-menu-message').textContent = message
  }

  eatFruitFromMenu(kind) {
    if (!this.player.eatFruit(kind)) {
      this.updateFruitMenu(
        this.player.hearts >= this.player.maxHearts
          ? 'YOUR HEARTS ARE FULL'
          : 'YOU DO NOT HAVE THAT ITEM'
      )
      return false
    }
    this.updateFruitMenu(kind === 'healthPotion' ? 'POTION USED!' : 'YUM!')
    this.quickSave()
    return true
  }

  closeFruitMenu() {
    document.getElementById('fruit-menu').style.display = 'none'
    this.isPaused = false
    this.clearActiveMenuButtons()
  }
  
  showItemPopup(text, sprite = SWORD_SPRITE) {
    this.isPaused = true
    document.getElementById('item-popup').style.display = 'flex'
    document.getElementById('item-popup-text').textContent = text

    const canvas = document.getElementById('item-popup-icon')
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    canvas.hidden = !sprite
    if (sprite) this.drawPopupSprite(canvas, sprite)

    this.setActiveMenuButtons(['item-popup-ok'])
  }

  drawPopupSprite(canvas, sprite) {
    drawPixelSpriteToCanvas(canvas, sprite)
  }

  dismissItemPopup() {
    this.isPaused = false
    document.getElementById('item-popup').style.display = 'none'
    this.clearActiveMenuButtons()
    this.hud.updateButtonHud()
  }

  getCurrentZone() {
    return this.world ? this.world.getCurrentZone(this.player.position) : null
  }
}
